import { keepPreviousData, queryOptions } from "@tanstack/react-query";

import { ProjectHeaderName } from "@/api/createApiClient";
import { useApi } from "@/api/hooks/useApi";
import type { Api } from "@/api/types";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { QUERY_KEYS } from "@/query-keys";

/** Query options for an API client, so router loaders can prefetch the same cache entries outside React. */
export const selfAccountQueries = (api: Api<null>["api"]) => {
  return {
    sessionUserInitial: (projectId: string | undefined, keepPreviousDataOption?: boolean) =>
      queryOptions({
        queryKey: QUERY_KEYS.SELF(projectId),
        queryFn: () => {
          const headers: Record<string, string> = {};

          // Initial session user HTTP call at the top of the loader tree doesn't have access to a client with project id set,
          // so we need to set it manually.
          if (projectId != null) {
            headers[ProjectHeaderName] = projectId;
          }

          return api.getSelfV2({ headers });
        },
        select: commonAPIDataSelector,
        staleTime: 1000 * 60 * 5,
        gcTime: Infinity,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousDataOption ? keepPreviousData : undefined,
      }),
    notificationSettings: () =>
      queryOptions({
        queryKey: QUERY_KEYS.SELF_ACCOUNT_NOTIFICATION_SETTINGS(),
        queryFn: () => api.getAccountNotificationSettingsV1(),
        select: commonAPIDataSelector,
      }),
    listLinkedProjects: () =>
      queryOptions({
        queryKey: QUERY_KEYS.SELF_PROJECTS(),
        queryFn: () => api.getSelfProjectsV1(),
        select: (data) => commonAPIDataSelector(data).projects,
        staleTime: 1000 * 60 * 5,
        gcTime: Infinity,
      }),
  };
};

export const useSelfAccountQueries = () => selfAccountQueries(useApi());
